import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/api';

const PO_DOC_TYPES = ['Purchase Order', 'Purchase Request'];
const PR_DOC_TYPES = ['Production Order'];

const norm = (v) => String(v ?? '').trim();

/** Planned + Released rows can be copied onto a new PO / production order (Closed is skipped). */
const isImportableRecStatus = (st) => {
  const s = norm(st);
  return s === 'Planned' || s === 'Released';
};

const docTypeMatches = (docType, allowedList) => {
  const d = norm(docType);
  return allowedList.some((w) => norm(w) === d);
};

function summarizeRecommendations(recs) {
  const byType = {};
  for (const r of recs) {
    const dt = norm(r.doc_type) || '(unknown type)';
    byType[dt] = (byType[dt] || 0) + 1;
  }
  return Object.entries(byType)
    .map(([k, v]) => `${v}× ${k}`)
    .join(', ');
}

/**
 * Pick an MRP scenario (run) and load planned recommendations for PO or Production import.
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {'purchase'|'production'} mode
 * @param {(recs: object[], run: object|null) => void} onApply — filtered recommendations + run header
 */
export default function MrpRecommendationImportDialog({ open, onClose, mode, onApply }) {
  const [runs, setRuns] = useState([]);
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRunId, setLoadingRunId] = useState(null);
  const [err, setErr] = useState('');

  const wantTypes = mode === 'purchase' ? PO_DOC_TYPES : PR_DOC_TYPES;
  const title = mode === 'purchase'
    ? 'Import from order recommendation (purchase)'
    : 'Import from order recommendation (production)';

  useEffect(() => {
    if (!open) return;
    setScenarioSearch('');
    setErr('');
    setLoading(true);
    api.get('/mrp/runs')
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setRuns(rows.filter(
          (r) => r.row_type === 'run'
            && String(r.status || '') === 'Completed'
            && Number(r.rec_count || 0) > 0
        ));
      })
      .catch((e) => {
        setErr(e.message || 'Failed to load MRP scenarios');
        setRuns([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const filteredRuns = useMemo(() => {
    const q = scenarioSearch.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((r) => {
      const name = norm(r.run_name).toLowerCase();
      const desc = norm(r.description).toLowerCase();
      const proj = norm(r.project_number).toLowerCase();
      const rid = String(r.run_id ?? '').toLowerCase();
      return (
        name.includes(q) ||
        desc.includes(q) ||
        proj.includes(q) ||
        rid.includes(q)
      );
    });
  }, [runs, scenarioSearch]);

  const selectRun = async (runId) => {
    setLoadingRunId(runId);
    setErr('');
    try {
      const data = await api.get(`/mrp/recommendations/${runId}`);
      const recs = data.recommendations || [];
      if (recs.length === 0) {
        setErr('This scenario has no recommendation rows.');
        return;
      }

      const filtered = recs.filter(
        (r) => docTypeMatches(r.doc_type, wantTypes)
          && isImportableRecStatus(r.status)
          && r.item_id != null
      );

      if (filtered.length === 0) {
        const summary = summarizeRecommendations(recs);
        const modeHint =
          mode === 'purchase'
            ? 'Purchase import only includes Purchase Order and Purchase Request lines (not Production Order or Inventory Transfer).'
            : 'Production import only includes Production Order lines (not purchase or transfer rows).';
        const stPlanned = recs.filter((r) => norm(r.status) === 'Planned').length;
        const stReleased = recs.filter((r) => norm(r.status) === 'Released').length;
        const stOther = recs.length - stPlanned - stReleased;
        const statusNote =
          stOther > 0
            ? ` Status: ${stPlanned} planned, ${stReleased} released, ${stOther} other/closed.`
            : ` Status: ${stPlanned} planned, ${stReleased} released.`;
        setErr(
          `No lines match this import. ${modeHint} This scenario: ${summary}.${statusNote}`
        );
        return;
      }
      onApply(filtered, data.run || null);
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to load recommendations');
    } finally {
      setLoadingRunId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="relative bg-white rounded-xl shadow-2xl border border-sap-border w-[min(560px,94vw)] max-h-[min(480px,85vh)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl shrink-0">
          <span className="text-sap-text text-xs font-semibold">{title}</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none" aria-label="Close">✕</button>
        </div>
        <div className="px-4 py-2 text-2xs text-sap-textLight border-b border-sap-border shrink-0">
          Choose a completed MRP scenario. {mode === 'purchase' ? 'Purchase / PR' : 'Production'} lines with status Planned or Released are imported (Closed is skipped).
        </div>
        <div className="shrink-0 px-4 py-2 border-b border-sap-border bg-slate-50/80">
          <label htmlFor="mrp-import-scenario-search" className="sr-only">
            Search scenarios
          </label>
          <input
            id="mrp-import-scenario-search"
            type="search"
            value={scenarioSearch}
            onChange={(e) => setScenarioSearch(e.target.value)}
            disabled={loading || runs.length === 0}
            placeholder="Search scenario name, description, project, run ID…"
            autoComplete="off"
            className="w-full h-8 rounded-lg border border-sap-border bg-white px-3 text-xs text-sap-text placeholder:text-sap-textLight/70 focus:border-sap-blue focus:outline-none focus:ring-2 focus:ring-sap-blue/15 disabled:opacity-50"
          />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-10 text-center text-xs text-sap-textLight">Loading scenarios…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40 z-[1]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-2xs text-sap-textLight uppercase tracking-wider">Scenario</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs text-sap-textLight uppercase tracking-wider w-28">Project</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs text-sap-textLight uppercase tracking-wider w-16">Recs</th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((r) => (
                  <tr
                    key={r.run_id}
                    className="border-t border-sap-border/20 hover:bg-sap-blue/5 cursor-pointer"
                    onClick={() => !loadingRunId && selectRun(r.run_id)}
                  >
                    <td className="px-3 py-2">
                      <div className="font-semibold text-sap-blue">{r.run_name || `Run #${r.run_id}`}</div>
                      {r.description && r.description !== r.run_name && (
                        <div className="text-2xs text-sap-textLight mt-0.5 line-clamp-2">{r.description}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sap-text tabular-nums">{r.project_number ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.rec_count}</td>
                  </tr>
                ))}
                {runs.length === 0 && !loading && (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-sap-textLight">
                      No completed MRP scenarios with recommendations found.
                    </td>
                  </tr>
                )}
                {runs.length > 0 && filteredRuns.length === 0 && !loading && (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-sap-textLight">
                      No scenarios match “{scenarioSearch.trim()}”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {err && (
          <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-100 shrink-0">{err}</div>
        )}
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl shrink-0 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-white transition-all"
          >
            Cancel
          </button>
        </div>
        {loadingRunId != null && (
          <div className="absolute inset-0 rounded-xl bg-white/60 flex items-center justify-center text-xs font-semibold text-sap-text">
            Loading…
          </div>
        )}
      </div>
    </div>
  );
}
